"use client";

import { Button, useMediaQuery } from "@kyan/ui";

import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useMarket } from "#/store/app/portfolio/market";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { useShowTradePanel } from "#/store/app/tradePanel/showTradePanel";
import { useTradePanelTab } from "#/store/app/tradePanel/tradePanelTab";
import { useMaturity } from "#/store/options/page/maturity";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionLimitPrice } from "#/store/options/tradePanel/optionLimitPrice";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { useOptionTradeType } from "#/store/options/tradePanel/optionTradeType";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { usePerpTradeType } from "#/store/perps/tradePanel/perpTradeType";
import { usePerpUnit } from "#/store/perps/tradePanel/perpUnit";
import { type Position } from "#/types/positions";
import { TradeDirection } from "#/types/trade";
import {
  getOptionInstrumentProperties,
  getPerpInstrumentProperties,
} from "#/utils/instrument.utils";
import { nextMaturities } from "#/utils/maturities";

interface ClosePositionProps {
  position: Position;
}

export const ClosePosition: React.FC<ClosePositionProps> = ({ position }) => {
  const { isSm } = useMediaQuery();
  const { setMarketByUnderlying } = useMarket();
  const { setOption } = useOption();
  const { setMaturity } = useMaturity();
  const { setPerpSizeInputValue } = usePerpSize();
  const { setPerpIsBuy } = usePerpIsBuy();
  const { setOptionSize } = useOptionSize();
  const { setTradePanelTab } = useTradePanelTab();
  const { setPerpTradeType } = usePerpTradeType();
  const { setShowTradePanel } = useShowTradePanel();
  const { setOptionTradeType } = useOptionTradeType();
  const { setOptionLimitPrice } = useOptionLimitPrice();
  const { setPerpUnit } = usePerpUnit();
  const { market } = usePositionsDrawerMarket();
  const { isLiquidated } = usePortfolioValidations(market);

  const isShort = position.size < 0; // negative positions are "shorts", positives are "longs"
  const closeDirection = isShort ? "buy" : "sell";

  const isPerps = position.instrumentType === "perps";
  const setSize = isPerps ? setPerpSizeInputValue : setOptionSize;
  const setTradeType = isPerps ? setPerpTradeType : setOptionTradeType;

  const handleClose = () => {
    setTradeType("market");
    setSize(Math.abs(position.size).toString());
    setTradePanelTab(position.instrumentType);
    setShowTradePanel(true);

    if (isPerps) {
      const { underlying } = getPerpInstrumentProperties(position?.instrument);
      setMarketByUnderlying(underlying);
      setPerpIsBuy(isShort);
      setPerpUnit(underlying);
      return;
    }

    const { strike, maturity, optionType, marketPair, underlying } =
      getOptionInstrumentProperties(position.instrument);

    if (nextMaturities().includes(maturity)) setMaturity(maturity);

    const optionToClose = {
      isBuy: isShort,
      strike,
      isCall: optionType === "call",
      maturity,
      direction: closeDirection as TradeDirection,
      marketPair,
      underlying,
      instrument_name: position.instrument,
    };
    setOptionLimitPrice("");
    setOption(optionToClose);
  };

  return (
    <Button
      size="sm"
      className="capitalize"
      disabled={isLiquidated}
      variant={isSm ? "action" : "primary"}
      onClick={handleClose}
    >
      {closeDirection} to close
    </Button>
  );
};
